import { useCallback, useEffect, useRef } from 'react';
import { SearchProvider } from '../../contexts/SearchProvider';
import { v4 as uuidv4 } from 'uuid';
import shopify from '../../../js/shopify';
import helpers from '../../../js/helpers';

export const DEFAULT_TRENDING_QUERIES = [
    {
        searchQuery: "Cocoon Swaddle",
        popularity: 3
    },
    {
        searchQuery: "Jersey Sleeping Bag",
        popularity: 2
    },
    {
        searchQuery: "Butterfly Cardi",
        popularity: 2
    },
    {
        searchQuery: "Bodywear Singlet",
        popularity: 1
    }
]
/**
 * Creates a provider for SearchSpring search implementation
 * 
 * @param {Object} props - The props for the SearchSpringProvider component
 * @param {React.ReactNode} props.children - Child components
 * @param {Object} props.settings - Settings for the SearchSpring search
 * @param {string} props.searchType - Type of search ('search', 'plp', 'header')
 * @returns {JSX.Element} A SearchProvider with SearchSpring-specific implementation
 */
export function SearchSpringProvider({ children, settings, searchType = 'search' }) {
  // Create refs to maintain state across re-renders
  const providerRef = useRef({
    initialized: false,
    siteId: '',
    productsPerPage: 24,
    isProcessingRequest: false,
    trending: DEFAULT_TRENDING_QUERIES,
    currentCollection: null,
  });

  const provider = providerRef.current;

  // Initialize the provider
  const initialize = useCallback((searchSettings) => {
    if (provider.initialized) return;

    // Set site ID and products per page
    provider.siteId = searchSettings?.site_id || '';
    provider.productsPerPage = searchSettings.products_per_page || 24;
    provider.initialized = true;
    
    // Set up event listeners
    window.addEventListener('searchrequest', handleSearchRequest);
    window.addEventListener('searchrequest-suggestions', handleSuggestionsRequest);
    window.addEventListener('searchrequest-filter-applied', handleFilterRequest);
    window.addEventListener('searchrequest-sort-applied', handleSortRequest);
    window.addEventListener('searchrequest-nextpage', handlePaginationRequest);
    window.addEventListener('searchrequest-sortchanged', handleSortRequest);
    
    // Fetch initial trending queries
    fetchTrendingResults();
    
    return () => {
      window.removeEventListener('searchrequest', handleSearchRequest);
      window.removeEventListener('searchrequest-suggestions', handleSuggestionsRequest);
      window.removeEventListener('searchrequest-filter-applied', handleFilterRequest);
      window.removeEventListener('searchrequest-sort-applied', handleSortRequest);
      window.removeEventListener('searchrequest-nextpage', handlePaginationRequest);
      window.removeEventListener('searchrequest-sortchanged', handleSortRequest);
    };
  }, []);

  // Helper function to get common SearchSpring parameters
  const getCommonParams = useCallback(() => {
    return {
      userId: getSSCookieId('ssUserId'),
      sessionId: getSSCookieId('ssSessionIdNamespace'),
      pageLoadId: uuidv4(),
      domain: helpers.getDomain(),
      redirectResponse: 'native',
      resultsFormat: 'json',
      siteId: provider.siteId,
    };
  }, []);

  // Helper function to get or create a SearchSpring cookie ID
  const getSSCookieId = useCallback((cookie) => {
    // Helper function to read a cookie
    function getCookie(name) {
      const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
      return match ? match[2] : null;
    }

    // Helper function to set a cookie with expiration and path
    function setCookie(name, value, days = 365) {
      const expires = new Date(Date.now() + days * 86400000).toUTCString();
      document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=Lax`;
    }

    let ssId = getCookie(cookie);
    if (!ssId) {
      ssId = uuidv4();
      setCookie(cookie, ssId);
    }
    return ssId;
  }, []);

  // Helper function to build URL query parameters
  const buildQueryParams = useCallback((params) => {
    return new URLSearchParams(params).toString();
  }, []);

  // Handle search requests
  const handleSearchRequest = useCallback(async (event) => {
    const { query, type, collection, productsPerPage } = event.detail;
    
    if (!provider.siteId) return;
    if (provider.isProcessingRequest) return;
    
    provider.isProcessingRequest = true;

    // Store the collection handle if available
    if (type === 'plp' && collection) {
      provider.currentCollection = collection;
    }
    
    // Show spinner for UI feedback
    window.dispatchEvent(
      new CustomEvent('show-spinner', {
        detail: {
          id: type === 'search' ? 'spinner-search' : 'spinner-collection',
        },
      })
    );
    
    try {
      const results = await fetchProductResults({ q: query }, '', 1, productsPerPage);
      provider.isProcessingRequest = false;
    } catch (error) {
      console.error('Error performing SearchSpring search:', error);
      provider.isProcessingRequest = false;
      
      window.dispatchEvent(
        new CustomEvent('hide-spinner', {
          detail: {
            id: type === 'search' ? 'spinner-search' : 'spinner-collection',
          },
        })
      );
    }
  }, []);

  // Handle suggestions requests
  const handleSuggestionsRequest = useCallback(async (event) => {
    const { query, type } = event.detail;
    
    if (!provider.siteId) return;
    if (provider.isProcessingRequest) return;
    
    provider.isProcessingRequest = true;
    
    try {
      const autocompleteResults = await fetchAutocompleteResults({ q: query });
      provider.isProcessingRequest = false;
    } catch (error) {
      console.error('Error performing SearchSpring suggestions search:', error);
      provider.isProcessingRequest = false;
    }
  }, []);

  // Handle filter requests
  const handleFilterRequest = useCallback(async (event) => {
    const { query, type, filters_object } = event.detail;
    
    if (!provider.siteId) return;
    if (provider.isProcessingRequest) return;
    
    provider.isProcessingRequest = true;
    
    // Show spinner for UI feedback
    window.dispatchEvent(
      new CustomEvent('show-spinner', {
        detail: {
          id: type === 'search' ? 'spinner-search' : 'spinner-collection',
        },
      })
    );
    
    // Convert filters_object to SearchSpring filter string
    let filterString = '';
    if (filters_object && Object.keys(filters_object).length > 0) {
      filterString = '&';
      Object.entries(filters_object).forEach(([key, values]) => {
        if (Array.isArray(values) && values.length > 0) {
          values.forEach(value => {
            filterString += `filter.${key}=${encodeURIComponent(value)}&`;
          });
        }
      });
      // Remove trailing &
      filterString = filterString.slice(0, -1);
    }
    
    try {
      const results = await fetchProductResults({ q: query }, filterString);
      provider.isProcessingRequest = false;
    } catch (error) {
      console.error('Error performing SearchSpring filtered search:', error);
      provider.isProcessingRequest = false;
      
      window.dispatchEvent(
        new CustomEvent('hide-spinner', {
          detail: {
            id: type === 'search' ? 'spinner-search' : 'spinner-collection',
          },
        })
      );
    }
  }, []);

  // Handle sort requests
  const handleSortRequest = useCallback(async (event) => {
    const { query, type, sortOption } = event.detail;
    
    if (!provider.siteId) return;
    if (provider.isProcessingRequest) return;
    
    provider.isProcessingRequest = true;
    
    // Show spinner for UI feedback
    window.dispatchEvent(
      new CustomEvent('show-spinner', {
        detail: {
          id: type === 'search' ? 'spinner-search' : 'spinner-collection',
        },
      })
    );
    
    // Map sort options to SearchSpring format
    let sort = '';
    switch (sortOption) {
      case 'Newest':
        sort = 'created_at,desc';
        break;
      case 'Popular':
        sort = 'top_sellers,desc';
        break;
      case 'LowToHigh':
        sort = 'price,asc';
        break;
      case 'HighToLow':
        sort = 'price,desc';
        break;
      case 'Featured':
        sort = 'custom,asc';
        break;
      default:
        sort = 'score,desc'; // Default relevance sort
    }
    
    try {
      const results = await fetchProductResults({ q: query, sort });
      provider.isProcessingRequest = false;
    } catch (error) {
      console.error('Error performing SearchSpring sorted search:', error);
      provider.isProcessingRequest = false;
      
      window.dispatchEvent(
        new CustomEvent('hide-spinner', {
          detail: {
            id: type === 'search' ? 'spinner-search' : 'spinner-collection',
          },
        })
      );
    }
  }, []);

  // Handle pagination requests
  const handlePaginationRequest = useCallback(async (event) => {
    const { query, type, page, productsPerPage } = event.detail;
    
    if (!provider.siteId) return;
    if (provider.isProcessingRequest) return;
    
    provider.isProcessingRequest = true;
    
    // Show spinner for UI feedback
    window.dispatchEvent(
      new CustomEvent('show-spinner', {
        detail: {
          id: type === 'search' ? 'spinner-search' : 'spinner-collection',
        },
      })
    );
    
    try {
      const results = await fetchProductResults({ q: query }, '', page, productsPerPage);
      provider.isProcessingRequest = false;
    } catch (error) {
      console.error('Error performing SearchSpring paginated search:', error);
      provider.isProcessingRequest = false;
      
      window.dispatchEvent(
        new CustomEvent('hide-spinner', {
          detail: {
            id: type === 'search' ? 'spinner-search' : 'spinner-collection',
          },
        })
      );
    }
  }, []);

  // Fetch search results from SearchSpring
  const fetchProductResults = useCallback(async (query, filterString = '', page = 1, perPage = null) => {
    if (!provider.siteId) return null;
    
    try {
      const baseUrl = `https://${provider.siteId}.a.searchspring.io/api/search/search.json`;
      let url = '';

      const queryParams = {
        ...query,
        ...getCommonParams(),
        resultsPerPage: perPage || provider.productsPerPage,
        page
      };

      // Add collection filtering if present
      if (provider.currentCollection && !query.q) {
        queryParams.filter = `collection_handle:${provider.currentCollection}`;
      }
      
      if (filterString.length > 0 && filterString !== '?') {
        url = `${baseUrl}?${buildQueryParams(queryParams)}${filterString}`;
      } else {
        url = `${baseUrl}?${buildQueryParams(queryParams)}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
      });
      
      if (!response.ok) {
        throw new Error(`Search API responded with status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Transform the search results to match Shopify format
      const productIds = data.results.map(result => result.id);
      const transformedProducts = await shopify.getShopifyProductList(productIds);
      
      // Extract facets/filters for sidebar
      const facets = data.facets?.map(facet => ({
        key: facet.field,
        label: facet.label || facet.field,
        values: facet.values.map(val => ({
          key: val.value,
          label: val.label || val.value,
          count: val.count
        }))
      })) || [];
      
      // Dispatch events with search results
      window.dispatchEvent(
        new CustomEvent('searchrequest-products-complete', {
          detail: {
            query: query.q || '',
            products: transformedProducts,
            totalResults: data.pagination.totalResults,
            filters: facets,
            type: 'search',
            page: data.pagination.currentPage,
            isFiltering: filterString.length > 0
          },
        })
      );
      
      // Also dispatch a filter-list event for the sidebar
      window.dispatchEvent(
        new CustomEvent('filter-list', {
          detail: {
            type: 'search',
            filters: facets,
          },
        })
      );
      
      return { products: transformedProducts, pagination: data.pagination, filters: facets };
    } catch (err) {
      console.error('Error fetching search results:', err);
      return null;
    }
  }, [provider.siteId, provider.productsPerPage, getCommonParams, buildQueryParams]);

  // Fetch autocomplete suggestions from SearchSpring
  const fetchAutocompleteResults = useCallback(async (query) => {
    if (!provider.siteId) return null;
    
    try {
      const autocompleteUrl = `https://${provider.siteId}.a.searchspring.io/api/search/autocomplete.json`;
      const url = `${autocompleteUrl}?${buildQueryParams({ ...query, ...getCommonParams() })}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
      });
      
      if (!response.ok) {
        throw new Error(`Autocomplete API responded with status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Dispatch event with suggestions results
      window.dispatchEvent(
        new CustomEvent('searchrequest-suggestions-complete', {
          detail: {
            default: data.suggested || [],
            type: 'header'
          },
        })
      );
      
      return data;
    } catch (err) {
      console.error('Error fetching autocomplete results:', err);
      return null;
    }
  }, [provider.siteId, getCommonParams, buildQueryParams]);

  // Fetch trending queries from SearchSpring
  const fetchTrendingResults = useCallback(async () => {
    if (!provider.siteId) return null;
    
    try {
      const trendingUrl = `https://${provider.siteId}.a.searchspring.io/api/suggest/trending`;
      const url = `${trendingUrl}?${buildQueryParams(getCommonParams())}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
      });
      
      if (!response.ok) {
        throw new Error(`Trending API responded with status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Update trending queries in provider state
      provider.trending = data.trending || DEFAULT_TRENDING_QUERIES;
      
      return data;
    } catch (err) {
      console.error('Error fetching trending results:', err);
      return null;
    }
  }, [provider.siteId, getCommonParams, buildQueryParams]);

  // Fetch product recommendations from SearchSpring
  const fetchRecommendationResults = useCallback(async (productId, tags) => {
    if (!provider.siteId) return null;
    
    try {
      const recsUrl = `https://${provider.siteId}.a.searchspring.io/boost/${provider.siteId}/recommend`;
      const url = `${recsUrl}?${buildQueryParams({ product: productId, tags, ...getCommonParams() })}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
      });
      
      if (!response.ok) {
        throw new Error(`Recommendation API responded with status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Transform the recommendation results to match Shopify format
      const productIds = data.results?.map(result => result.id) || [];
      const transformedProducts = await shopify.getShopifyProductList(productIds);
      
      return transformedProducts;
    } catch (err) {
      console.error('Error fetching recommendation results:', err);
      return null;
    }
  }, [provider.siteId, getCommonParams, buildQueryParams]);

  // Clean up event listeners when component unmounts
  useEffect(() => {
    return () => {
      window.removeEventListener('searchrequest', handleSearchRequest);
      window.removeEventListener('searchrequest-suggestions', handleSuggestionsRequest);
      window.removeEventListener('searchrequest-filter-applied', handleFilterRequest);
      window.removeEventListener('searchrequest-sort-applied', handleSortRequest);
      window.removeEventListener('searchrequest-nextpage', handlePaginationRequest);
      window.removeEventListener('searchrequest-sortchanged', handleSortRequest);
    };
  }, []);

  // Create the provider object that our base SearchProvider will use
  const searchSpringProvider = {
    initialize,
    handleSearchRequest,
    handleSuggestionsRequest,
    handleFilterRequest,
    handleSortRequest,
    handlePaginationRequest,
    fetchRecommendationResults,
    trending: provider.trending,
  };

  return (
    <SearchProvider provider={searchSpringProvider} settings={settings} searchType={searchType}>
      {children}
    </SearchProvider>
  );
}

export default SearchSpringProvider;